<script setup>
/**
 * Notifications.
 *
 * These preferences used to be two sections buried near the bottom of a very
 * long profile page, laid out as a checkbox matrix with an Email column and a
 * Push column. Text.com gives notifications a page of its own, and reads far
 * better for it: one card per delivery channel, one switch per event.
 *
 * Switches apply immediately, as everywhere else in the redesign - there is no
 * Save button on this page.
 */
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import {
  hasPushPermissions,
  requestPushPermissions,
  verifyServiceWorkerExistence,
} from 'dashboard/helper/pushHelper.js';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { NOTIFICATION_TYPES } from './constants';

import SettingsPageShell from 'dashboard/components-next/Settings/SettingsPageShell.vue';
import SettingsSection from 'dashboard/components-next/Settings/SettingsSection.vue';
import SettingsToggleRow from 'dashboard/components-next/Settings/SettingsToggleRow.vue';
import AudioNotifications from './AudioNotifications.vue';

const { t } = useI18n();
const store = useStore();
const { accountId } = useAccount();

const emailFlags = useMapGetter(
  'userNotificationSettings/getSelectedEmailFlags'
);
const pushFlags = useMapGetter('userNotificationSettings/getSelectedPushFlags');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);

const hasBrowserPermission = ref(false);

// The three SLA events only mean something on an account that has SLAs.
const notificationTypes = computed(() => {
  const slaEnabled = isFeatureEnabledonAccount.value(
    accountId.value,
    FEATURE_FLAGS.SLA
  );
  if (slaEnabled) return NOTIFICATION_TYPES;

  return NOTIFICATION_TYPES.filter(
    type => !type.value.startsWith('sla_missed_')
  );
});

const hasPushAPISupport = 'Notification' in window;

const readPushSubscription = () => {
  verifyServiceWorkerExistence(registration =>
    registration.pushManager.getSubscription().then(subscription => {
      hasBrowserPermission.value = Boolean(subscription);
    })
  );
};

onMounted(() => {
  if (hasPushPermissions()) readPushSubscription();
  store.dispatch('userNotificationSettings/get');
});

// The record is empty until the first render's fetch resolves, so both getters
// start out undefined.
const flagsFor = channel =>
  (channel === 'email' ? emailFlags.value : pushFlags.value) ?? [];

const isEnabled = (channel, value) =>
  flagsFor(channel).includes(`${channel}_${value}`);

const toggleFlag = async (channel, value) => {
  const flag = `${channel}_${value}`;
  const flags = flagsFor(channel);
  const updated = flags.includes(flag)
    ? flags.filter(selected => selected !== flag)
    : [...flags, flag];

  try {
    await store.dispatch('userNotificationSettings/update', {
      selectedEmailFlags: channel === 'email' ? updated : flagsFor('email'),
      selectedPushFlags: channel === 'push' ? updated : flagsFor('push'),
    });
    useAlert(t('PROFILE_SETTINGS.FORM.API.UPDATE_SUCCESS'));
  } catch (error) {
    useAlert(t('PROFILE_SETTINGS.FORM.API.UPDATE_ERROR'));
  }
};

const onRequestPermissions = value => {
  if (value) {
    requestPushPermissions({
      onSuccess: () => {
        hasBrowserPermission.value = true;
      },
    });
    return;
  }

  verifyServiceWorkerExistence(registration =>
    registration.pushManager
      .getSubscription()
      .then(subscription => subscription?.unsubscribe())
      .finally(() => {
        hasBrowserPermission.value = false;
      })
  );
};
</script>

<template>
  <SettingsPageShell
    :title="$t('NOTIFICATION_SETTINGS.TITLE')"
    :description="$t('NOTIFICATION_SETTINGS.DESCRIPTION')"
  >
    <SettingsSection
      :title="$t('PROFILE_SETTINGS.FORM.EMAIL_NOTIFICATIONS_SECTION.TITLE')"
      :description="
        $t('PROFILE_SETTINGS.FORM.EMAIL_NOTIFICATIONS_SECTION.NOTE')
      "
      :eyebrow="$t('NOTIFICATION_SETTINGS.NOTIFY_ME_ABOUT')"
    >
      <SettingsToggleRow
        v-for="type in notificationTypes"
        :key="`email_${type.value}`"
        :label="$t(type.label)"
        :model-value="isEnabled('email', type.value)"
        @update:model-value="toggleFlag('email', type.value)"
      />
    </SettingsSection>

    <SettingsSection
      :title="$t('PROFILE_SETTINGS.FORM.PUSH_NOTIFICATIONS_SECTION.TITLE')"
      :description="$t('PROFILE_SETTINGS.FORM.PUSH_NOTIFICATIONS_SECTION.NOTE')"
    >
      <SettingsToggleRow
        v-if="hasPushAPISupport"
        v-model="hasBrowserPermission"
        :label="$t('PROFILE_SETTINGS.FORM.NOTIFICATIONS.BROWSER_PERMISSION')"
        @update:model-value="onRequestPermissions"
      />
      <SettingsToggleRow
        v-for="type in notificationTypes"
        :key="`push_${type.value}`"
        :label="$t(type.label)"
        :model-value="isEnabled('push', type.value)"
        @update:model-value="toggleFlag('push', type.value)"
      />
    </SettingsSection>

    <SettingsSection
      :title="$t('PROFILE_SETTINGS.FORM.AUDIO_NOTIFICATIONS_SECTION.TITLE')"
      :description="
        $t('PROFILE_SETTINGS.FORM.AUDIO_NOTIFICATIONS_SECTION.NOTE')
      "
    >
      <div class="p-4">
        <AudioNotifications />
      </div>
    </SettingsSection>
  </SettingsPageShell>
</template>
